import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo, useState } from 'react';
import useDashboardsEffects from '../../hooks/context/effects/useDashboardsEffects';
import useDashboardsContext from '../../hooks/context/useDashboardsContext';
import { Dashboard, DashboardQueriesType, DashboardQuery } from '../../types/files';
import { AuthContext } from '../AuthContext';
import storage from '../../utils/storage';

export type OverviewQueries = {
  [key: string]: number | string | string[];
};

export type OverviewQueriesList = {
  [key: string]: OverviewQueries;
};

export type StatusGroupList = {
  [key: string]: Dashboard[];
};

type DashboardsContextProps = {
  dashboardsLoaded: boolean;
  setDashboardsLoaded: Dispatch<SetStateAction<boolean>>;
  dashboards: Dashboard[];
  setDashboards: Dispatch<SetStateAction<Dashboard[]>>;
  dashboardsCollection: Record<string, DashboardQueriesType[]>;
  setDashboardsCollection: Dispatch<SetStateAction<Record<string, DashboardQueriesType[]>>>;
  externalDashboards: Dashboard[];
  setExternalDashboards: Dispatch<SetStateAction<Dashboard[]>>;
  dashboardsQueries: DashboardQuery[],
  setDashboardsQueries: Dispatch<SetStateAction<DashboardQuery[]>>,
  mappedOverviewQueries: Map<string, any>;
  isPublicView: boolean;
  setPublicView: Dispatch<SetStateAction<boolean>>;
  isShared: boolean;
  setShared: Dispatch<SetStateAction<boolean>>;
};

export const DashboardsContext = createContext<DashboardsContextProps>({
  dashboardsLoaded: false,
  setDashboardsLoaded: () => {},
  dashboards: [],
  setDashboards: () => {},
  dashboardsCollection: {},
  setDashboardsCollection: () => {},
  externalDashboards: [],
  setExternalDashboards: () => {},
  dashboardsQueries: [],
  setDashboardsQueries: () => {},
  mappedOverviewQueries: new Map(),
  isPublicView: false,
  setPublicView: () => {},
  isShared: false,
  setShared: () => {},
});

export const OverviewQueryTitles = ['Digest', 'Team', 'Key People', 'Note Metrics', 'Investment Thesis', 'Location', 'Website', 'Crunchbase Page', 'LinkedIn'];
export const DisplayQueryTitles = ['Description', 'Website', 'LinkedIn', 'Location', 'Team', 'Metrics'];
export const CompanyInfoFields = ['Description', 'Stage', 'Sector', 'Team', 'Deck', 'Deal overview'];

const DashboardsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsQueries,
    setIsPublicView,
    setIsShared,
  } = useDashboardsContext();
  useDashboardsEffects(userGroup);

  const [dashboardsCollection, setDashboardsCollection] = useState<Record<string, DashboardQueriesType[]>>({});

  const contextValue = useMemo(() => ({
    dashboardsLoaded,
    setDashboardsLoaded,
    dashboards,
    setDashboards,
    dashboardsCollection,
    setDashboardsCollection,
    externalDashboards,
    setExternalDashboards,
    dashboardsQueries,
    setDashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    setPublicView: setIsPublicView,
    isShared,
    setShared: setIsShared,
  }), [
    dashboardsLoaded,
    setDashboardsLoaded,
    dashboards,
    setDashboards,
    dashboardsCollection,
    setDashboardsCollection,
    externalDashboards,
    setExternalDashboards,
    dashboardsQueries,
    setDashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    setIsPublicView,
    isShared,
    setIsShared,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ["Dashboards", contextValue]);

  return (
    <DashboardsContext.Provider value={contextValue}>
      {children}
    </DashboardsContext.Provider>
  );
};

export default DashboardsProvider;
